import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { useSaveJobPostDetail } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { IJobDetailsForm, JobDetailsFormMapper } from '../../JobDetailsForm';
import {
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageLocationState,
} from '../Types';

interface IUseSaveJobPostOptions {
  triggers: (keyof IJobDetailsForm)[];
  targetStep?: CreatePermanentJobPostingSteps;
  withRedirect?: boolean;
}

export const useSaveJobPost = (
  controller: UseFormReturn<IJobDetailsForm>,
  jobPostingId: string | undefined,
  { triggers, targetStep, withRedirect = true }: IUseSaveJobPostOptions,
) => {
  const { isPending, mutate } = useSaveJobPostDetail();

  const location = useLocation().state as TCreatePermanentJobPostingPageLocationState;
  const navigate = useNavigate();

  const hasFormErrors = triggers.some((field) => !!controller.getFieldState(field).error);

  const onSave = useCallback(async () => {
    if (!jobPostingId) {
      return;
    }

    const isValid = await controller.trigger(triggers);

    if (!isValid) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    mutate(
      {
        id: jobPostingId,
        payload: JobDetailsFormMapper.toSaveJobPostDetailPayload(controller.getValues()),
      },
      {
        onError: (err) => showErrorMessage({ error: err }),
        onSuccess: () => {
          if (!withRedirect) {
            return;
          }

          if (location?.editMode) {
            navigate(-1);
            return;
          }

          if (targetStep) {
            navigate(`/permanent-jobs/postings/${jobPostingId}/create/${targetStep}`);
          }
        },
      },
    );
  }, [
    controller,
    jobPostingId,
    location?.editMode,
    mutate,
    navigate,
    targetStep,
    triggers,
    withRedirect,
  ]);

  return {
    isPending,
    hasFormErrors,
    onSave,
  };
};
